import React, { useCallback, useEffect, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import {
  Group as GroupIcon,
  Share as ShareIcon,
  TouchApp as TouchAppIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { api, SuperShowcaseViewStat } from '../../services/api';

export type ShowcaseViewStatsMap = Map<number, SuperShowcaseViewStat>;

/**
 * Loads per-showcase view counts (admin-only endpoint) while `enabled`.
 * Failures are swallowed — the counts are a nice-to-have on top of the list.
 */
export function useShowcaseViewStats(enabled: boolean): {
  stats: ShowcaseViewStatsMap | null;
  reload: (refresh?: boolean) => void;
} {
  const [stats, setStats] = useState<ShowcaseViewStatsMap | null>(null);

  const reload = useCallback((refresh = false) => {
    api.getSuperShowcaseViewStats({ refresh })
      .then((result) => setStats(new Map(result.items.map((item) => [item.showcaseId, item]))))
      .catch((err) => {
        console.warn('Failed to load showcase view counts:', err);
      });
  }, []);

  useEffect(() => {
    if (enabled) reload();
  }, [enabled, reload]);

  return { stats, reload };
}

const EMPTY_STAT: Omit<SuperShowcaseViewStat, 'showcaseId'> = {
  views: 0,
  viewers: 0,
  hotspotClicks: 0,
  sharers: 0,
  shares: 0,
  lastViewedAt: null,
};

function formatLastViewed(iso: string | null): string {
  if (!iso) return 'Not viewed yet';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Not viewed yet';
  return `Last viewed ${date.toLocaleString('en-SG', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}`;
}

/** One compact line: views · unique agents · area clicks · agents who shared. */
export const ShowcaseViewStatsLine: React.FC<{ stat: SuperShowcaseViewStat | undefined }> = ({ stat }) => {
  const s = stat ?? EMPTY_STAT;
  const item = (icon: React.ReactNode, value: number, label: string) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
      {icon}
      <Box component="span" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
        {value.toLocaleString('en-SG')}
      </Box>
      {label}
    </Box>
  );
  return (
    <Tooltip
      title={`All-time, from Google Analytics (may lag up to a day). ${formatLastViewed(s.lastViewedAt)}. `
        + `${s.shares.toLocaleString('en-SG')} share${s.shares === 1 ? '' : 's'} in total (copy, WhatsApp or share sheet).`}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: 1.5,
          rowGap: 0.25,
          mt: 0.75,
          fontSize: '0.75rem',
          color: 'var(--color-text-muted)',
          '& svg': { fontSize: 15 },
        }}
      >
        {item(<VisibilityIcon />, s.views, s.views === 1 ? 'view' : 'views')}
        {item(<GroupIcon />, s.viewers, s.viewers === 1 ? 'agent' : 'agents')}
        {item(<TouchAppIcon />, s.hotspotClicks, s.hotspotClicks === 1 ? 'area click' : 'area clicks')}
        {item(<ShareIcon />, s.sharers, s.sharers === 1 ? 'agent shared' : 'agents shared')}
      </Box>
    </Tooltip>
  );
};
